/**
 * @file: CampaignRewardHistoryList.tsx
 *
 * Campaign-reward tab content of the rewards-history screen. Campaign
 * rewards are auto-computed at order finish and settled without an
 * approval flow, so each card is simpler than the product tab: the order
 * context header + one row per campaign reward (campaign name + amount +
 * settled/pending badge). Grouped by the originating order to mirror the
 * product tab's per-order card layout. `isShare` selects share rewards.
 */

import { Hr } from '@euler/app/components/Hr';
import { StackLayout } from '@euler/app/components/StackLayout';
import { TouchCopyable } from '@euler/app/components/TouchCopyable';
import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import Euler from '@euler/components/layout';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useInfiniteListWithCursor } from '@euler/functions/useInfiniteListWithCursor';
import { useScreenContentContainerStyle } from '@euler/functions/wrapNavigatorScreen';
import {
  MyCampaignRewardItem,
  MyCampaignRewardOrderGroup,
} from '@euler/model/campaign/MyCampaignRewardRecord';
import { useServiceFactory } from '@euler/services/factory';
import { formatMoney } from '@euler/utils/formatMoney';
import { formatDateTime } from '@euler/utils/formatters';
import { memo, useCallback, useEffect } from 'react';
import {
  ActivityIndicator,
  FlatList,
  ListRenderItem,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const kLimit = 5;
const keyExtractor = (g: MyCampaignRewardOrderGroup) =>
  `${g.orderNo}:${g.rewards[0]?.id ?? ''}`;

export const CampaignRewardHistoryList = ({
  isShare,
}: {
  isShare?: boolean;
}) => {
  const { bottom } = useSafeAreaInsets();
  const { rpx } = useResponsiveContext();
  const { userService } = useServiceFactory();
  const contentContainerStyle = useScreenContentContainerStyle();

  const { data, status, fetch, fetchMore } = useInfiniteListWithCursor<
    MyCampaignRewardOrderGroup,
    string
  >({
    limit: kLimit,
    query: async nextKey => {
      return await userService.searchMyCampaignRewards(
        { isShare },
        nextKey,
        kLimit,
      );
    },
  });

  const renderItem: ListRenderItem<MyCampaignRewardOrderGroup> = useCallback(
    ({ item }) => <Cell group={item} isShare={isShare} />,
    [isShare],
  );

  useEffect(() => {
    void fetch();
  }, [fetch]);

  return (
    <FlatList
      data={data}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      onEndReached={() => fetchMore()}
      ItemSeparatorComponent={() => <Hr color="#ddd" height={rpx(1)} />}
      contentContainerStyle={[
        { flexGrow: 1, padding: rpx(0), paddingBottom: bottom },
        contentContainerStyle,
      ]}
      ListHeaderComponent={
        !status.loadedDataLength && status.isLoading ? (
          <Center
            css={`
              flex: 1;
            `}
          >
            <ActivityIndicator />
          </Center>
        ) : null
      }
      ListEmptyComponent={
        !status.isLoading ? (
          <Center
            css={`
              flex: 1;
              padding-top: ${rpx(60)}px;
            `}
          >
            <Typography.Text color="#999" size={rpx(13)}>
              暂无活动奖励记录
            </Typography.Text>
          </Center>
        ) : null
      }
      ListFooterComponent={
        status.loadedDataLength > 0 && status.isLoading ? (
          <ActivityIndicator
            css={`
              margin-top: ${rpx(10)}px;
            `}
          />
        ) : status.loadedDataLength > 0 && !status.hasMore ? (
          <Typography.Text
            color={'#999'}
            size={rpx(12)}
            mt={rpx(10)}
            align="center"
          >
            没有更多了
          </Typography.Text>
        ) : null
      }
    />
  );
};

const Cell = memo(
  ({
    group,
    isShare,
    style,
  }: {
    group: MyCampaignRewardOrderGroup;
    isShare?: boolean;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const infoFontSize = rpx(12);

    const copyText = [
      `订单号码: ${group.orderNo}`,
      `车牌号码: ${group.licensePlateNo ?? '-'}`,
      group.vehicleName ? `车型名称: ${group.vehicleName}` : undefined,
      group.orderCreatedAt
        ? `进厂时间: ${formatDateTime(group.orderCreatedAt)}`
        : undefined,
      group.orderFinishedAt
        ? `完工时间: ${formatDateTime(group.orderFinishedAt)}`
        : undefined,
      `记录时间: ${formatDateTime(group.rewardedAt)}`,
      ...group.rewards.map(
        r =>
          `${r.campaignName || '活动奖励'}: ${formatMoney(r.amount, {
            prefix: true,
          })}, ${r.settled ? '已发放' : '待发放'}`,
      ),
    ]
      .filter(Boolean)
      .join('\n');

    return (
      <StackLayout
        direction="column"
        style={style}
        css={`
          padding: ${rpx(10)}px ${rpx(15)}px;
        `}
      >
        <StackLayout
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={rpx(4)}
          mb={rpx(5)}
        >
          <Typography.Text medium size={rpx(16)} lineHeight={rpx(26)} flex={1}>
            订单 {group.orderNo}
          </Typography.Text>
          <TouchCopyable text={copyText}>
            <Typography.Text light size={rpx(10)}>
              复制记录信息
            </Typography.Text>
          </TouchCopyable>
        </StackLayout>
        <StackLayout mb={rpx(8)}>
          <Typography.Text size={infoFontSize}>
            车牌号码: {group.licensePlateNo ?? '-'}
          </Typography.Text>
          {group.vehicleName ? (
            <Typography.Text size={infoFontSize}>
              车型名称: {group.vehicleName}
            </Typography.Text>
          ) : null}
          {group.orderCreatedAt ? (
            <Typography.Label size={infoFontSize}>
              进厂时间: {formatDateTime(group.orderCreatedAt)}
            </Typography.Label>
          ) : null}
          {group.orderFinishedAt ? (
            <Typography.Label size={infoFontSize}>
              完工时间: {formatDateTime(group.orderFinishedAt)}
            </Typography.Label>
          ) : null}
          <Typography.Label size={infoFontSize}>
            记录时间: {formatDateTime(group.rewardedAt)}
          </Typography.Label>
        </StackLayout>
        <StackLayout
          direction="column"
          alignItems="stretch"
          divider={<Hr color="#d7d7d7" />}
          gap={rpx(8)}
          css={`
            background-color: #eee;
            padding: ${rpx(10)}px;
            border-radius: ${rpx(5)}px;
          `}
        >
          {group.rewards.map(reward => (
            <RewardRow key={reward.id} reward={reward} isShare={isShare} />
          ))}
        </StackLayout>
      </StackLayout>
    );
  },
);

const RewardRow = memo(
  ({
    reward,
    isShare,
    style,
  }: {
    reward: MyCampaignRewardItem;
    isShare?: boolean;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View style={style}>
        <StackLayout direction="row" alignItems="center">
          <Euler.View flex={1}>
            <Typography.Label
              color="#fa8c16"
              size={rpx(12)}
              lineHeight={rpx(18)}
              medium
              numberOfLines={1}
            >
              {reward.campaignName || '活动奖励'}
              {isShare ? ' (分成)' : ''}
            </Typography.Label>
          </Euler.View>
          <Typography.Label size={rpx(12)} lineHeight={rpx(18)} bold flex={1}>
            {formatMoney(reward.amount, { prefix: true })}
          </Typography.Label>
          <Euler.View flex={1} flexDirection="row" justifyContent="flex-end">
            <View
              css={`
                background-color: ${reward.settled
                  ? StatusColors.Success
                  : StatusColors.Warn};
                padding: 0 ${rpx(5)}px;
                border-radius: ${rpx(3)}px;
              `}
            >
              <Typography.Label
                color="#fff"
                size={rpx(10)}
                lineHeight={rpx(18)}
              >
                {reward.settled ? '已发放' : '待发放'}
              </Typography.Label>
            </View>
          </Euler.View>
        </StackLayout>
      </View>
    );
  },
);
